import { useQuery } from "@tanstack/react-query";
import { clearAllWritingDrafts } from "@/lib/writing-draft-storage";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/contexts/RoleContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Pencil, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface ExtProfile {
  first_name: string | null;
  last_name: string | null;
  specializations: string[];
  education_level: string | null;
  atar: string | null;
  scholarships_awards: string | null;
  years_experience: number;
  teaching_style: string | null;
  fun_fact: string | null;
  student_impact: string | null;
  profile_completed: boolean;
  profile_photo_url: string | null;
}

export function MobileTutorProfile() {
  const { userId, userProfile } = useRole();
  const navigate = useNavigate();

  const { data: location } = useQuery({
    queryKey: ["mobile-tutor-loc", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data } = await supabase
        .from("user_locations")
        .select("locations(name)")
        .eq("user_id", userId)
        .maybeSingle();
      return data;
    },
    enabled: !!userId,
  });

  const { data: ext } = useQuery({
    queryKey: ["mobile-tutor-ext", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data } = await supabase
        .from("tutor_extended_profiles" as any)
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      return data as unknown as ExtProfile | null;
    },
    enabled: !!userId,
  });

  const locationName = location?.locations ? (location.locations as { name: string }).name : null;
  const fullName = userProfile?.full_name || `${ext?.first_name || ""} ${ext?.last_name || ""}`.trim() || "Tutor";
  const initials = fullName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  const specs = ext?.specializations || [];

  const EDUCATION_MAP: Record<string, string> = {
    at_school: "At School",
    ug_in_progress: "Undergraduate (In Progress)",
    ug_completed: "Undergraduate (Completed)",
    pg_in_progress: "Postgraduate (In Progress)",
    pg_completed: "Postgraduate (Completed)",
    diploma: "Diploma Holder",
  };

  // Determine if profile is incomplete
  const requiredFields = [ext?.first_name, ext?.last_name, ext?.education_level, ext?.fun_fact];
  const isIncomplete = !ext?.profile_completed || requiredFields.some((f) => !f);

  return (
    <div className="space-y-4 pt-2 pb-4">
      {/* Incomplete banner */}
      {isIncomplete && (
        <div className="flex items-center gap-2.5 rounded-[14px] px-4 py-3" style={{ background: "#FEF3F0", border: "1px solid #F5C4B3" }}>
          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: "#F44321" }} />
          <p className="text-[12px] font-medium" style={{ color: "#C03010" }}>
            Profile incomplete — fill in all required fields
          </p>
        </div>
      )}

      {/* Hero card */}
      <div className="rounded-[14px] py-6 px-4 flex flex-col items-center" style={{ background: "#ffffff", border: "0.5px solid #e8e0d8" }}>
        <Avatar className="h-20 w-20 mb-3">
          <AvatarImage src={ext?.profile_photo_url || userProfile?.avatar_url || undefined} />
          <AvatarFallback className="text-lg font-bold" style={{ background: "#F2C9C0", color: "#C03010" }}>
            {initials}
          </AvatarFallback>
        </Avatar>
        <p className="text-[17px] font-bold" style={{ color: "#1B2D5B" }}>{fullName}</p>
        {locationName && <p className="text-[13px] mt-0.5" style={{ color: "#9a8a7a" }}>{locationName}</p>}

        {specs.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1.5 mt-3">
            {specs.map((s) => (
              <span key={s} className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ background: "#FDECEA", color: "#C03010" }}>
                {s}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Detail sections */}
      <DetailSection title="Personal Details">
        <Field label="First name" value={ext?.first_name} required />
        <Field label="Last name" value={ext?.last_name} required />
      </DetailSection>

      <DetailSection title="Education & Qualifications">
        <Field label="Qualification" value={ext?.education_level ? EDUCATION_MAP[ext.education_level] || ext.education_level : null} required />
        <Field label="ATAR score" value={ext?.atar} required />
        <Field label="Scholarships" value={ext?.scholarships_awards} />
      </DetailSection>

      <DetailSection title="Teaching">
        <Field label="Years experience" value={ext?.years_experience != null ? String(ext.years_experience) : null} required />
        <Field label="Teaching style" value={ext?.teaching_style} />
      </DetailSection>

      <DetailSection title="About Me">
        <Field label="Fun fact" value={ext?.fun_fact} required />
        <Field label="Student results" value={ext?.student_impact} />
      </DetailSection>

      {/* Edit CTA */}
      <button
        onClick={() => navigate("/tutor-profile")}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-[14px] font-bold text-white"
        style={{ background: "#F44321", borderRadius: 12 }}
      >
        <Pencil className="h-4 w-4" />
        Edit Profile
      </button>

      {/* Sign Out */}
      <button
        onClick={async () => {
          await clearAllWritingDrafts(); // explicit user sign-out only
          await supabase.auth.signOut();
          toast.success("Signed out");
          navigate("/auth");
        }}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-[14px] font-semibold"
        style={{ background: "#ffffff", border: "0.5px solid #e8e0d8", color: "#9a8a7a", borderRadius: 12 }}
      >
        <LogOut className="h-4 w-4" />
        Sign Out
      </button>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: "#9a8a7a" }}>{title}</p>
      <div className="rounded-[14px] divide-y divide-[#e8e0d8]" style={{ background: "#ffffff", border: "0.5px solid #e8e0d8" }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, required }: { label: string; value: string | null | undefined; required?: boolean }) {
  const hasValue = !!value && value.trim() !== "";
  return (
    <div className="px-4 py-3 flex items-center justify-between gap-2" style={{ borderColor: "#e8e0d8" }}>
      <span className="text-[12px]" style={{ color: "#9a8a7a" }}>{label}</span>
      {hasValue ? (
        <span className="text-[13px] font-medium text-right max-w-[60%] truncate" style={{ color: "#1B2D5B" }}>{value}</span>
      ) : required ? (
        <span className="text-[12px] font-medium" style={{ color: "#C03010" }}>Not set</span>
      ) : (
        <span className="text-[12px]" style={{ color: "#9a8a7a" }}>Optional</span>
      )}
    </div>
  );
}